import { useMemo } from 'react';
import * as Y from 'yjs';
import { createNode } from '@/features/canvas/engine/primitives/persistence/create-node';
import { createEdgeFromConnection } from '@/features/canvas/engine/edges/create-edge';
import {
  serializeForClipboard,
  deserializeIntoYjs,
  writeCanvasToClipboard,
} from '@/features/canvas/engine/primitives/clipboard';
import {
  bringToFront,
  bringForward,
  sendBackward,
  sendToBack,
} from '@/features/canvas/engine/primitives/persistence/z-order';
import {
  type NodeActions,
  type ExpandDirection,
} from '@/features/canvas/engine/wiring/contexts/node-actions-context';
import { EXPAND_GAP, DUPLICATE_OFFSET } from '@/features/canvas/utils/constants';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';

/**
 * Builds the NodeActions bundle that CanvasEditor wires into NodeActionsContext.
 * Each method mutates the supplied Yjs doc directly under a single transaction
 * keyed on `clientId`.
 *
 * Returns null when `doc` is null so callers can pass through to a null context
 * value without conditional rendering. `setPendingSelectId` is the setter used
 * by `onExpand` to focus the newly created node after layout.
 *
 * Memoised on `[doc, clientId]` — the original site only depended on `[doc]`,
 * but every method closes over `doc.clientID` so re-running when the client id
 * changes (e.g. server-issued reassignment) keeps the closures in sync.
 */
export function useNodeActionsImpl(
  doc: Y.Doc | null,
  clientId: number,
  setPendingSelectId: (id: string | null) => void,
): NodeActions | null {
  const binding = useCanvasBinding();
  return useMemo<NodeActions | null>(() => {
    if (!doc) return null;
    const roots = canvasRoots(doc, binding);
    return {
      onDelete: (nodeId: string) => {
        const nodes = roots.nodes;
        const node = nodes.get(nodeId);
        if (!node) return;
        if (node.get('locked') === true) return;

        // Frame delete cascades to direct children — same contract
        // useFrameDelete enforces on the keyboard delete path. Walk
        // both data.childOrder AND any node whose parentId points at
        // the frame (defensive: catches concurrent-attach races).
        const isFrame = node.get('type') === 'frame';
        const childIds = new Set<string>();
        if (isFrame) {
          const data = node.get('data') as Y.Map<unknown> | undefined;
          const childOrder = data?.get('childOrder') as Y.Array<string> | undefined;
          if (childOrder) {
            for (const id of childOrder.toArray()) childIds.add(id);
          }
          nodes.forEach((n) => {
            if (!(n instanceof Y.Map)) return;
            if (n.get('parentId') === nodeId) {
              const id = n.get('id');
              if (typeof id === 'string') childIds.add(id);
            }
          });
        }

        doc.transact(() => {
          const zOrder = roots.zOrder;
          const arr = zOrder.toArray();
          const indicesToDelete: number[] = [];
          for (const id of childIds) {
            const idx = arr.indexOf(id);
            if (idx !== -1) indicesToDelete.push(idx);
            nodes.delete(id);
          }
          const frameIdx = arr.indexOf(nodeId);
          if (frameIdx !== -1) indicesToDelete.push(frameIdx);
          // Sort descending so earlier deletions don't shift later target indices.
          indicesToDelete.sort((a, b) => b - a);
          for (const idx of indicesToDelete) zOrder.delete(idx, 1);
          nodes.delete(nodeId);
        }, clientId);
      },
      onDuplicate: (nodeId: string) => {
        const nodes = roots.nodes;
        const node = nodes.get(nodeId);
        if (!node) return;
        const json = serializeForClipboard([node], []);
        const payload = JSON.parse(json);
        // Offset duplicate by 20px so it's visually distinct from original
        for (const n of payload.nodes) {
          n.position = { x: n.position.x + DUPLICATE_OFFSET, y: n.position.y + DUPLICATE_OFFSET };
        }
        // preserveOrphanParent: keep the node inside the same frame if it has
        // one — only the child was serialized, not the parent frame, so the
        // normal "null orphan" path would detach the duplicate from the frame.
        const { nodeIdMap } = deserializeIntoYjs(JSON.stringify(payload), doc, undefined, { preserveOrphanParent: true }, binding);
        // Select the copy so it reads as the new front-most, ready-to-drag
        // item — without selection the offset clone looks like it's behind.
        const newId = nodeIdMap.get(nodeId);
        if (newId) setPendingSelectId(newId);
      },
      onToggleLock: (nodeId: string) => {
        const nodes = roots.nodes;
        const node = nodes.get(nodeId);
        if (!node) return;
        const nextLocked = !node.get('locked');
        doc.transact(() => {
          node.set('locked', nextLocked);
          // Frame lock cascades to its direct children: locking the
          // frame should mean nothing inside is editable / deletable
          // either, mirroring the user's expectation that the frame
          // is the unit of grouping. Same on unlock — the children
          // come back with the frame.
          if (node.get('type') === 'frame') {
            nodes.forEach((child) => {
              if (!(child instanceof Y.Map)) return;
              if (child.get('parentId') !== nodeId) return;
              child.set('locked', nextLocked);
            });
          }
        }, clientId);
      },
      onExpand: (nodeId: string, direction: ExpandDirection) => {
        const nodes = roots.nodes;
        const node = nodes.get(nodeId);
        if (!node) return;
        const type = node.get('type') as string | undefined;
        const pos = node.get('position') as { x: number; y: number } | undefined;
        const size = node.get('size') as { width: number; height: number } | null | undefined;
        if (!pos || !type) return;
        const w = size?.width ?? 160;
        const h = size?.height ?? 120;
        const gap = EXPAND_GAP;
        const offsets: Record<ExpandDirection, { x: number; y: number }> = {
          top:    { x: 0,          y: -(h + gap) },
          right:  { x: w + gap,    y: 0          },
          bottom: { x: 0,          y: h + gap    },
          left:   { x: -(w + gap), y: 0          },
        };
        const { x, y } = offsets[direction];
        const newPos = { x: pos.x + x, y: pos.y + y };
        // Copy data fields from source node so shape-specific props (shapeKind, color, etc.) are preserved.
        const sourceData = node.get('data');
        const overrides: Record<string, unknown> = {};
        if (sourceData instanceof Y.Map) {
          sourceData.forEach((value, key) => { overrides[key] = value; });
        }
        const newNode = createNode(doc, type as Parameters<typeof createNode>[1], newPos, overrides, clientId, size ?? null, binding);
        const newId = newNode.get('id') as string;
        // Connect source → new node with a visual edge using drop-{dir} handles.
        // drop-* handles are always mounted, so the edge stays renderable when
        // neither endpoint is selected. Target handle is the opposite side so
        // the path runs cleanly between the two facing edges.
        const opposite: Record<ExpandDirection, ExpandDirection> = {
          top: 'bottom', bottom: 'top', left: 'right', right: 'left',
        };
        createEdgeFromConnection(doc, clientId, '', {
          source: nodeId,
          target: newId,
          sourceHandle: `drop-${direction}`,
          targetHandle: `drop-${opposite[direction]}`,
        }, binding);
        setPendingSelectId(newId);
      },
      onDeleteMany: (nodeIds: string[]) => {
        const nodes = roots.nodes;
        doc.transact(() => {
          const zOrder = roots.zOrder;
          const arr = zOrder.toArray();
          // Collect indices to delete, then sort descending so earlier deletions
          // don't shift the indices of subsequent targets.
          const indicesToDelete: number[] = [];
          for (const nodeId of nodeIds) {
            const node = nodes.get(nodeId);
            if (!node || node.get('locked') === true) continue;
            const idx = arr.indexOf(nodeId);
            if (idx !== -1) indicesToDelete.push(idx);
            nodes.delete(nodeId);
          }
          indicesToDelete.sort((a, b) => b - a);
          for (const idx of indicesToDelete) {
            zOrder.delete(idx, 1);
          }
        }, clientId);
      },
      onDuplicateMany: (nodeIds: string[]) => {
        const nodes = roots.nodes;
        const yNodes = nodeIds.map((id) => nodes.get(id)).filter((n): n is Y.Map<unknown> => n != null);
        if (yNodes.length === 0) return;
        const json = serializeForClipboard(yNodes, []);
        const payload = JSON.parse(json);
        for (const n of payload.nodes) {
          n.position = { x: n.position.x + DUPLICATE_OFFSET, y: n.position.y + DUPLICATE_OFFSET };
        }
        deserializeIntoYjs(JSON.stringify(payload), doc, undefined, { preserveOrphanParent: true }, binding);
      },
      // Group: wrap N selected nodes in a new frame (Figma "Group"
      // semantics). Mirrors useFrameGroup — bbox the selection,
      // createNode('frame'), rebase children to frame-local, set
      // parentId + extent='parent', push to data.childOrder. Inlined
      // here (vs. dispatching to useFrameGroup) for the same reason
      // onUngroup is inlined: the hook can't be called inside the
      // useMemo body, and adding it at the top of useNodeActionsImpl
      // would break the `if (!doc) return null` early-return pattern.
      onGroupMany: (nodeIds: string[]) => {
        if (nodeIds.length < 2) return;
        const nodes = roots.nodes;

        // Pass 1: resolve absolute coords + skip locked/missing.
        type Resolved = {
          id: string;
          node: Y.Map<unknown>;
          absX: number;
          absY: number;
          width: number;
          height: number;
        };
        const resolved: Resolved[] = [];
        for (const id of nodeIds) {
          const node = nodes.get(id);
          if (!node) continue;
          if (node.get('locked') === true) continue;
          const pos = node.get('position') as { x: number; y: number } | undefined;
          const size = node.get('size') as { width: number; height: number } | null | undefined;
          if (!pos) continue;
          let absX = pos.x;
          let absY = pos.y;
          const parentId = node.get('parentId');
          if (typeof parentId === 'string') {
            const parent = nodes.get(parentId);
            const parentPos = parent?.get('position') as { x: number; y: number } | undefined;
            if (parentPos) { absX += parentPos.x; absY += parentPos.y; }
          }
          resolved.push({
            id, node, absX, absY,
            width: size?.width ?? 0,
            height: size?.height ?? 0,
          });
        }
        if (resolved.length < 2) return;

        // Bounding box.
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const r of resolved) {
          if (r.absX < minX) minX = r.absX;
          if (r.absY < minY) minY = r.absY;
          if (r.absX + r.width > maxX) maxX = r.absX + r.width;
          if (r.absY + r.height > maxY) maxY = r.absY + r.height;
        }
        const framePos = { x: minX, y: minY };
        const frameSize = { width: maxX - minX, height: maxY - minY };

        // One transact: createNode opens a nested transact (Yjs
        // coalesces — single update event = single undo step).
        // Hoist frameId so we can call setPendingSelectId after the
        // transact closes (without that, the user's mental "I'm
        // working with this group now" diverges from RF's selection
        // state, which would still hold the original child nodes).
        let frameId = '';
        doc.transact(() => {
          const frame = createNode(doc, 'frame', framePos, undefined, clientId, frameSize, binding);
          frameId = frame.get('id') as string;
          const data = frame.get('data') as Y.Map<unknown>;
          const childOrder = data.get('childOrder') as Y.Array<string>;
          for (const r of resolved) {
            r.node.set('position', { x: r.absX - minX, y: r.absY - minY });
            r.node.set('parentId', frameId);
            r.node.set('extent', 'parent');
            childOrder.push([r.id]);
          }
        }, clientId);

        // Flip selection to the new frame on the next Yjs round-trip
        // via applyPendingSelect (CanvasEngine consumes pendingSelectId
        // and clears every other node's `selected: true`). Same
        // mechanism onExpand uses for "select the just-created node."
        setPendingSelectId(frameId);
      },
      onCopy: (nodeId: string) => {
        const nodes = roots.nodes;
        const node = nodes.get(nodeId);
        if (!node) return;
        writeCanvasToClipboard(serializeForClipboard([node], []));
      },
      onCopyMany: (nodeIds: string[]) => {
        const nodes = roots.nodes;
        const yNodes = nodeIds.map((id) => nodes.get(id)).filter((n): n is Y.Map<unknown> => n != null);
        if (yNodes.length === 0) return;
        writeCanvasToClipboard(serializeForClipboard(yNodes, []));
      },
      onBringToFront: (nodeId: string) => bringToFront(doc, clientId, nodeId, binding),
      onSendToBack:   (nodeId: string) => sendToBack(doc, clientId, nodeId, binding),
      onBringForward: (nodeId: string) => bringForward(doc, clientId, nodeId, binding),
      onSendBackward: (nodeId: string) => sendBackward(doc, clientId, nodeId, binding),
      onUpdateStroke: (nodeId: string, strokeColor: string, strokeWidth: number) => {
        const nodes = roots.nodes;
        const node = nodes.get(nodeId);
        if (!node) return;
        doc.transact(() => {
          const data = node.get('data') as Y.Map<unknown> | undefined;
          if (data instanceof Y.Map) {
            data.set('strokeColor', strokeColor);
            data.set('strokeWidth', strokeWidth);
          }
        }, clientId);
      },
      // Ungroup mirrors useFrameUngroup: detach every child (rebase
      // position frame-relative → absolute, strip parentId + extent),
      // then drop the frame. Inlined here (vs. dispatching to the
      // hook) so the action stays available without an extra hook
      // call in the action surface — same pattern onDelete uses for
      // its frame-cascade.
      onUngroup: (nodeId: string) => {
        const nodes = roots.nodes;
        const frame = nodes.get(nodeId);
        if (!frame) return;
        if (frame.get('locked') === true) return;
        if (frame.get('type') !== 'frame') return; // safety: surface is frame-only

        const framePos = frame.get('position') as { x: number; y: number } | undefined;
        const fx = framePos?.x ?? 0;
        const fy = framePos?.y ?? 0;

        const data = frame.get('data') as Y.Map<unknown> | undefined;
        const childOrder = data?.get('childOrder') as Y.Array<string> | undefined;
        const childIds = new Set<string>(childOrder?.toArray() ?? []);
        nodes.forEach((n) => {
          if (!(n instanceof Y.Map)) return;
          if (n.get('parentId') === nodeId) {
            const id = n.get('id');
            if (typeof id === 'string') childIds.add(id);
          }
        });

        doc.transact(() => {
          for (const childId of childIds) {
            const child = nodes.get(childId) as Y.Map<unknown> | undefined;
            if (!child) continue;
            const localPos = child.get('position') as { x: number; y: number } | undefined;
            const cx = localPos?.x ?? 0;
            const cy = localPos?.y ?? 0;
            child.set('position', { x: fx + cx, y: fy + cy });
            child.set('parentId', null);
            child.delete('extent');
          }
          const zOrder = roots.zOrder;
          const arr = zOrder.toArray();
          const frameIdx = arr.indexOf(nodeId);
          if (frameIdx !== -1) zOrder.delete(frameIdx, 1);
          nodes.delete(nodeId);
        }, clientId);
      },
      // select-all is implemented inside the ReactFlow tree via useReactFlow — stub here
      onSelectAll: () => {},
    };
  }, [doc, clientId, setPendingSelectId, binding]);
}
